import { z } from "zod";
import { createEndpoint, createEndpointWithPagination } from "../createEndpoint";
import type { HttpClient, QueryParams } from "../httpClient";
import {
    AdminEmailFromProfileSchema,
    AdminEmailListMetaSchema,
    AdminEmailSchema,
    AdminEmailTagSchema,
    SendAdminEmailRequestSchema,
} from "../schemas/admin-emails";

export interface AdminEmailTagsQueryParams extends QueryParams {
    /** Case-insensitive substring match on tag name. */
    search?: string;
}

/** All filters combine (AND) and are cursor-safe. */
export interface AdminEmailsListQueryParams extends QueryParams {
    /** Only emails linked to this expert. */
    expert_id?: number;
    /** `sent` | `delivered` | `opened` | `bounced`. */
    status?: string;
    /** Sender profile key, e.g. `support`. */
    from?: string;
    /** Exact tag name, case-insensitive. */
    tag?: string;
    /** Only emails carrying **all** of these tag ids (max 25). */
    tag_ids?: number[];
    /** Case-insensitive substring match on recipient email or subject. */
    search?: string;
    /** 1-100, default 20. */
    limit?: number;
    cursor?: string;
}

export function createAdminEmailsApi(http: HttpClient, baseUrl: string) {
    const adminEmailsBaseUrl = `${baseUrl}/admin/emails`;

    return {
        /** Tag catalog with usage counts, including the system tags. */
        listTags: createEndpoint<
            z.infer<typeof AdminEmailTagSchema>[],
            unknown,
            AdminEmailTagsQueryParams
        >(http, "GET", "/tags", {
            base: adminEmailsBaseUrl,
            responseSchema: z.array(AdminEmailTagSchema),
        }),

        /** Sent admin emails, newest first. */
        list: createEndpointWithPagination<
            z.infer<typeof AdminEmailSchema>,
            z.infer<typeof AdminEmailListMetaSchema>,
            unknown,
            AdminEmailsListQueryParams
        >(http, "GET", "", {
            base: adminEmailsBaseUrl,
            itemSchema: AdminEmailSchema,
            metaSchema: AdminEmailListMetaSchema,
        }),

        /** Sender identities accepted as `from` by send(). */
        listFromProfiles: createEndpoint<
            z.infer<typeof AdminEmailFromProfileSchema>[]
        >(http, "GET", "/from-profiles", {
            base: adminEmailsBaseUrl,
            responseSchema: z.array(AdminEmailFromProfileSchema),
        }),

        /**
         * Send a one-off richtext email. The mail is dispatched synchronously;
         * on transport failure the stored row is deleted and the error is
         * returned, so only successfully dispatched emails exist.
         */
        send: createEndpoint<
            z.infer<typeof AdminEmailSchema>,
            z.input<typeof SendAdminEmailRequestSchema>
        >(http, "POST", "", {
            base: adminEmailsBaseUrl,
            responseSchema: AdminEmailSchema,
        }),

        /** A stored email including its current tracking status. */
        get: createEndpoint<
            z.infer<typeof AdminEmailSchema>,
            unknown,
            QueryParams,
            { emailId: number | string }
        >(http, "GET", "/:emailId", {
            base: adminEmailsBaseUrl,
            responseSchema: AdminEmailSchema,
            params: ["emailId"],
        }),
    };
}

export type AdminEmailsApi = ReturnType<typeof createAdminEmailsApi>;
